import React, { useState, useContext } from 'react';
import { RatingContext } from '../../context/RatingContext'; // Adjust path

const RateReviewModal = ({
  isOpen,
  onClose,
  items,
  itemRatings = {},
}) => {
  const { userId, submitReview } = useContext(RatingContext);

  const [selectedItem, setSelectedItem] = useState('');
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const [hoveredStar, setHoveredStar] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const unratedItems = items.filter(item => !itemRatings[item.id]);

  const handleSubmit = async () => {

    if (!selectedItem || rating === 0 || !review.trim()) {
    alert('Please select an item, provide a rating, and write a review');
    return;
  }
  
    if (!selectedItem || rating === 0) {
      alert('Please select an item and provide a rating');
      return;
    }

    try {
      setLoading(true);

      const selectedItemData = unratedItems.find(item => item.id === selectedItem);
      const productId = typeof selectedItem === 'string' && !isNaN(selectedItem)
        ? Number(selectedItem)
        : selectedItem;

      const result = await submitReview({
        product_id: selectedItemData.product_id,
        stars: rating,
        review,
      });

      alert(result.message || 'Review submitted successfully');

      // Reset form
      setSelectedItem('');
      setRating(0);
      setReview('');
      onClose();
    } catch (error) {
      console.error('Review submission failed:', error);
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleItemSelect = (itemId) => {
    setSelectedItem(itemId);
    setIsDropdownOpen(false);
  };

  const selectedItemData = unratedItems.find(item => item.id === selectedItem);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-sky-950">Rate & Review</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl font-bold">
            ×
          </button>
        </div>

        {/* Item Dropdown */}
        <div className="mb-6">
          <div className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full p-3 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between"
            >
              {selectedItemData ? (
                <div className="flex items-center gap-3">
                  <img src={selectedItemData.image} alt={selectedItemData.name} className="w-6 h-6 rounded-md" />
                  <span>{selectedItemData.name}</span>
                </div>
              ) : (
                <span className="text-gray-500">Choose an item...</span>
              )}
              <svg className={`w-5 h-5 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M19 9l-7 7-7-7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {isDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                {unratedItems.length === 0 ? (
                  <div className="p-3 text-gray-500 text-sm">No items available to rate</div>
                ) : (
                  unratedItems.map((item, index) => (
                    <button
                      key={item.id}
                      onClick={() => handleItemSelect(item.id)}
                      className="w-full p-3 text-left hover:bg-gray-50 flex items-center gap-3 border-b last:border-b-0"
                    >
                      <img src={item.image} alt={item.name} className="w-8 h-8 rounded-md object-contain" />
                      <span>{item.name}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Stars */}
        <div className="mb-6">
          <div className="flex justify-center gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredStar(star)}
                onMouseLeave={() => setHoveredStar(0)}
                disabled={loading}
              >
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill={star <= (hoveredStar || rating) ? '#fbbf24' : '#d1d5db'}
                >
                  <path
                    d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                    stroke={star <= (hoveredStar || rating) ? '#f59e0b' : '#9ca3af'}
                    strokeWidth="0.5"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            ))}
          </div>
          {rating > 0 && (
            <p className="text-center text-sm text-gray-600 mt-2">{rating} out of 5 stars</p>
          )}
        </div>

        {/* Review */}
        <div className="mb-6">
          <textarea
            value={review}
            onChange={(e) => setReview(e.target.value)}
            placeholder="Write your review"
            rows={4}
            className="w-full p-3 border-0 border-b border-gray-300 resize-none"
            disabled={loading}
            required
          />
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          className={`w-full px-5 py-4 rounded-2xl ${
            loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 text-white hover:bg-green-600'
          }`}
          disabled={loading}
        >
          {loading ? 'Submitting...' : 'Submit Review'}
        </button>
      </div>
    </div>
  );
};

export default RateReviewModal;
